import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useIntl } from "gatsby-plugin-react-intl"

import ImageContentful from "./ImageContentful"
import Link from "./Link"
import { media } from "../styles"

export const SubNavStyles = css`
  font-size: var(--font-size-15);
  font-weight: 400;
  margin: 0;
  text-transform: none;
  transform: translate3d(0, 0, 0);
`

export const NavGrid = styled.div`
  ${SubNavStyles}
  display: flex;
  flex-flow: row wrap;

  ${(props) =>
    !props.$mode &&
    css`
      ${NavGridCol} {
        &:nth-child(1),
        &:nth-child(2) {
          border-top: 0;
        }

        &:nth-child(odd) {
          border-right: 1px solid var(--color-greyWarm);
          padding-left: 0;
        }

        &:nth-child(even) {
          padding-right: 0;
        }
      }
    `}

  ${(props) =>
    (props.$mode === "crossnav" || props.$mode === "serviceoverview") &&
    css`
      justify-content: center;

      ${NavGridCol} {
        &:nth-child(1),
        &:nth-child(2) {
          border-top: none;
        }

        &:nth-child(odd) {
          border-right: 1px solid var(--color-greyWarm);
          padding-left: 0;
        }
      }

      @media ${media.md} {
        ${NavGridCol} {
          width: calc(50% - 25px);
        }
      }

      @media ${media.lg} {
        gap: 30px;

        ${NavGridCol} {
          border: none !important;
          padding: 0;
          width: calc(20% - 25px);
        }
      }
    `}

  ${(props) =>
    props.$mode === "serviceoverview" &&
    css`
      @media ${media.xl} {
        gap: 60px;
      }

      ${NavGridCol} {
        @media ${media.lg} {
          width: calc(25% - 60px);
        }

        h2,
        h3 {
          color: var(--color-spaceblue);

          @media ${media.xl} {
            font-size: var(--font-size-20);
            margin-bottom: 27px;
          }
        }
      }

      ${NavGridItem} {
        @media ${media.xl} {
          font-size: var(--font-size-17);
          margin-bottom: 22px;
        }

        a {
          color: var(--color-sun);

          &:hover {
            color: var(--color-spaceblue) !important;
          }
        }
      }
    `}


  ${(props) =>
    props.$mode === "topnav" &&
    css`
      ${NavGridCol} {
        border-right: 1px solid var(--color-greyWarm);
        border-color: var(--color-greyLight);
      }

      @media ${media.lg} {
        ${NavGridCol} {
          border-right: 1px solid var(--color-greyWarm);
          width: 25%;

          &:nth-child(1),
          &:nth-child(2),
          &:nth-child(3),
          &:nth-child(4),
          &:nth-child(5) {
            border-top: 0;
          }

          &:nth-child(3n) {
            border-right: 1px solid var(--color-greyWarm);
            padding: 15px;
          }

          &:nth-child(3n + 1) {
            padding-left: 15px;
          }

          &:nth-child(4n) {
            border-right: none;
          }

          ${(props) =>
            props.$isHidden === true &&
            css`
              @media ${media.xl} {
                display: none;
              }
            `}
        }
      }
    `}
`

export const NavGridCol = styled.div`
  border-top: 1px solid var(--color-greyWarm);
  padding: 15px;
  width: 50%;

  h2,
  h3 {
    color: var(--color-sage);
    font-size: var(--font-size-15);
    font-weight: 400;
    margin: 0 0 16px;
    text-transform: none;
  }

  ${(props) =>
    props.$isHidden === true &&
    (props.$mode === "crossnav" || props.$mode === "serviceoverview") &&
    css`
      @media ${media.md} {
        display: none;
      }
    `}

  ${(props) =>
    props.$isHidden === true &&
    props.$mode === "topnav" &&
    css`
      @media ${media.xl} {
        display: none;
      }
    `}
`

export const NavGridIcon = styled.div`
  color: ${(props) =>
    props.$color
      ? `var(--color-${props.$color}, var(--color-spaceblue))`
      : "var(--color-spaceblue)"};
  height: 24px;
  margin: 0 0 18px;
  width: 24px;

  ${(props) =>
    props.$hasLarge &&
    css`
      @media ${media.xl} {
        display: none;
      }
    `}

  ${(props) =>
    props.$size === "large" &&
    css`
      display: none;
      height: 48px;
      margin: 0 0 22px;
      width: 48px;

      @media ${media.xl} {
        display: block;
      }
    `}

  span,
  img,
  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`

export const NavGridList = styled.ul`
  font-size: var(--font-size-15);
  font-weight: 700;
  letter-spacing: -0.07px;
  line-height: 1.2;
  list-style: none;
  margin: 0 auto 10px;
  padding: 0;
  text-transform: none;

  &:last-child {
    margin-bottom: 0;
  }
`

export const NavGridItem = styled.li`
  margin: 0 0 10px 0;
  display: flex;
  font-size: var(--font-size-15);
  font-weight: 400;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--color-spaceblue);
    display: inline;
    padding: 0 !important;
    text-decoration: none;
    transition: color var(--transitionTime);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    .link-icon {
      height: auto;
      margin-left: 5px;
      width: 10px;
    }

    &:hover {
      color: var(--color-sun) !important;
    }
  }
`

const Header = ({ level = 3, children }) => {
  const Tag = `h${level}`
  return <Tag>{children}</Tag>
}

const NavigationGrid = ({
  basePath,
  handleFocus,
  headerLevel = 3,
  iconColor,
  items,
  mode,
  ...props
}) => {
  // using intl so that links also work for the blog
  const intl = useIntl()
  return (
    <NavGrid $mode={mode} {...props}>
      {items.map((item) => (
        <NavGridCol key={item.id} $mode={mode}>
          {item.icon ? (
            <NavGridIcon
              aria-hidden="true"
              $hasLarge={item.iconLarge ? true : false}
              $color={iconColor}
            >
              <ImageContentful image={item.icon} alt={item.name} inline />
            </NavGridIcon>
          ) : null}
          {item.iconLarge ? (
            <NavGridIcon $size="large" aria-hidden="true" $color={iconColor}>
              <ImageContentful image={item.iconLarge} alt={item.name} inline />
            </NavGridIcon>
          ) : null}
          <Header level={headerLevel}>{item.name}</Header>
          {item.items?.length > 0 ? (
            <NavGridList>
              {item.items.map((item) => (
                <NavGridItem key={item.slug}>
                  <Link
                    to={`/${intl.locale}${basePath || "/"}${item.slug}/`}
                    onFocus={handleFocus ? () => handleFocus() : null}
                    arrow={mode === "serviceoverview"}
                    intl={false}
                  >
                    {item.name}
                  </Link>
                </NavGridItem>
              ))}
            </NavGridList>
          ) : null}
        </NavGridCol>
      ))}
      {items.length % 2 === 1 ? (
        <NavGridCol
          $isHidden={
            mode === "crossnav" ||
            mode === "topnav" ||
            mode === "serviceoverview"
          }
          $mode={mode}
        ></NavGridCol>
      ) : null}
    </NavGrid>
  )
}

NavigationGrid.propTypes = {
  basePath: PropTypes.string,
  handleFocus: PropTypes.func,
  headerLevel: PropTypes.oneOf([2, 3, 4]),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.object,
      iconLarge: PropTypes.object,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      node_locale: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          slug: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  mode: PropTypes.oneOf(["crossnav", "topnav", "serviceoverview"]),
  iconColor: PropTypes.string,
}

export default NavigationGrid
